import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

/**
 * Placement for a menu that hangs off a form control and is PORTALED to the body.
 *
 * Every such menu in this folder shares it — Select, Autocomplete and TagsInput — because the thing
 * it solves is the same for all of them: a menu rendered in place is clipped by the first ancestor
 * that scrolls or hides its overflow, which on a form inside a modal is the modal's own body. The
 * menu ends up cut off a line into the list. Portaling it escapes the clip, and then it has to be
 * positioned by hand, which is what this does.
 *
 * Coordinates are viewport-relative because the menu is portaled with position:fixed — adding page
 * scroll would pin it to the top of the document whenever an inner container scrolls rather than
 * the window.
 */

const MARGIN = 8;
const GAP = 4;

const samePlacement = (a, b) => !!a && !!b && a.top === b.top && a.bottom === b.bottom
    && a.left === b.left && a.minWidth === b.minWidth && a.maxHeight === b.maxHeight;

export const usePlacement = (triggerRef, isOpen) => {
    const [placement, setPlacement] = useState(null);

    const measure = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom - MARGIN;
        const above = rect.top - MARGIN;
        const preferred = Math.round(window.innerHeight * 0.4);
        const openBelow = below >= Math.min(preferred, 160) || below >= above;
        const left = Math.max(MARGIN, Math.min(rect.left, window.innerWidth - rect.width - MARGIN));

        const next = {
            top: openBelow ? rect.bottom + GAP : null,
            bottom: openBelow ? null : window.innerHeight - rect.top + GAP,
            left,
            minWidth: rect.width,
            maxHeight: Math.max(120, Math.min(preferred, openBelow ? below : above)),
        };
        // Unchanged numbers keep the same object, so following the trigger re-renders only when it moves.
        setPlacement((prev) => (samePlacement(prev, next) ? prev : next));
    }, [triggerRef]);

    useLayoutEffect(() => {
        if (!isOpen) {
            setPlacement(null);
            return undefined;
        }
        measure();
        // The trigger also moves without a scroll or resize, e.g. a dialog re-centring as its content grows.
        let frame = requestAnimationFrame(function follow() {
            measure();
            frame = requestAnimationFrame(follow);
        });
        window.addEventListener('resize', measure);
        // Capture phase, so scrolls inside modals and other inner scroll containers are seen too.
        window.addEventListener('scroll', measure, true);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [isOpen, measure]);

    return placement;
};

export const useDismiss = (refs, isOpen, close) => {
    useEffect(() => {
        if (!isOpen) return undefined;
        const onPointerDown = (event) => {
            if (refs.some((ref) => ref.current?.contains(event.target))) return;
            close();
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, close]);
};

export const menuStyle = (placement) => ({
    position: 'fixed',
    top: placement.top ?? undefined,
    bottom: placement.bottom ?? undefined,
    left: placement.left,
    minWidth: placement.minWidth,
    maxHeight: placement.maxHeight,
});
